import React, {createContext, useContext, ReactNode, useMemo} from 'react';
import {ThemeProvider} from 'styled-components';
import {ThemeProvider as NativeThemeProvider} from 'styled-components/native';
import {useResponsiveFontSize} from '../hooks/useResponsiveFontSize';
import lightTheme, {WEB_FONT_SIZES} from './lightTheme';
import darkTheme from './darkTheme';
import {Theme} from './lightTheme';

// Create a context for the responsive theme
interface ResponsiveThemeContextType {
  theme: Theme;
  getFontSize: (size: number) => number;
}

const ResponsiveThemeContext = createContext<ResponsiveThemeContextType | null>(null);

interface ResponsiveThemeProviderProps {
  children: ReactNode;
  isDarkMode?: boolean;
}

/**
 * Provider that wraps the theme with responsive font sizes that update on window resize
 */
export const ResponsiveThemeProvider: React.FC<ResponsiveThemeProviderProps> = ({
  children,
  isDarkMode = false,
}) => {
  const {getFontSize} = useResponsiveFontSize();

  // Create responsive theme by taking the base theme and updating font sizes
  const theme = useMemo((): Theme => {
    const baseTheme = isDarkMode ? darkTheme : lightTheme;
    
    // Create a new theme object with responsive font sizes
    return {
      ...baseTheme,
      text: {
        ...baseTheme.text,
        // Override font sizes with responsive versions
        s1: getFontSize(WEB_FONT_SIZES.s1),
        s2: getFontSize(WEB_FONT_SIZES.s2),
        s3: getFontSize(WEB_FONT_SIZES.s3),
        s4: getFontSize(WEB_FONT_SIZES.s4),
        s5: getFontSize(WEB_FONT_SIZES.s5),
        s55: getFontSize(WEB_FONT_SIZES.s55),
        s6: getFontSize(WEB_FONT_SIZES.s6),
        s7: getFontSize(WEB_FONT_SIZES.s7),
        s8: getFontSize(WEB_FONT_SIZES.s8),
        s85: getFontSize(WEB_FONT_SIZES.s85),
        s89: getFontSize(WEB_FONT_SIZES.s89),
        s9: getFontSize(WEB_FONT_SIZES.s9),
        s10: getFontSize(WEB_FONT_SIZES.s10),
        s11: getFontSize(WEB_FONT_SIZES.s11),
        s12: getFontSize(WEB_FONT_SIZES.s12),
      },
    };
  }, [isDarkMode, getFontSize]);

  const contextValue: ResponsiveThemeContextType = {
    theme,
    getFontSize,
  };

  return (
    <ResponsiveThemeContext.Provider value={contextValue}>
      <NativeThemeProvider theme={theme}>
        <ThemeProvider theme={theme}>
          {children}
        </ThemeProvider>
      </NativeThemeProvider>
    </ResponsiveThemeContext.Provider>
  );
};

/**
 * Hook to access the responsive theme context
 */
export const useResponsiveTheme = (): ResponsiveThemeContextType => {
  const context = useContext(ResponsiveThemeContext);
  if (!context) {
    throw new Error('useResponsiveTheme must be used within a ResponsiveThemeProvider');
  }
  return context;
};
